<template>
    <div class="md-layout">
        <div :class="disableFilter ? '' : 'ontop'" class="md-layout-item md-size-100">
            <md-card>
                <md-card-content>
                    <collapse
                        :class="disableFilter ? 'collapse-panel-zindex' : 'collapse-panel'"
                        :collapse="['Filters']"
                        icon="keyboard_arrow_down"
                        :color-collapse="!disableFilter ? 'success' : ''"
                        :disabled="disableFilter"
                    >
                        <template slot="md-collapse-pane-1">
                            <process-charge-overview-toolbar
                                @onFilterProcessCharges="handleFilterChanged"
                                :team-region-id="user.teamRegionId"
                                :is-cost-approval-feature-enabled="isCostApprovalFeatureEnabled"
                                :is-charge-approval-feature-enabled="isChargeApprovalFeatureEnabled"
                                :service-packages="servicePackages"
                                :active-tab="activeTab"
                            />
                        </template>
                    </collapse>
                </md-card-content>
            </md-card>
        </div>
        <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
            <md-card class="custom-card">
                <tabs
                    :tab-name="tabNames"
                    color-button="success"
                    class="custom-tab-list"
                    @switch-panel="switchTab"
                    :selected-panel="replaceDashToSpacesAndCapitalizeFirstLetter(activeTab)"
                >
                    <template v-for="(tabName, index) in tabNames" :slot="`tab-pane-${index + 1}`">
                        <div class="pull-right review-contents" :key="tabName">
                            <component
                                class="review-contents"
                                :is="tabComponents[index]"
                                :filter="filters"
                                :service-packages="servicePackages"
                                @totalCostDropDownUpdated="updateTotalCostDropDown"
                                @totalChargeDropDownUpdated="updateTotalChargeDropDown"
                                ref="processChargeTab"
                            />
                        </div>
                    </template>
                </tabs>
            </md-card>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import {
    PROCESS_TOTALS_CONSTANTS,
    FEATURE_NAMES,
    REVIEW_CHARGES_ACTIVE_FILTERS,
    HUB_NAMES,
    HUB_ACTIONS,
    SIGNALR_CONNECTION_STATE
} from '@/utils/constants';
import { Collapse, Tabs } from '@/components';
import { handleRequests, showErrorMessage } from '@/helpers';
import InvoiceAdjustmentPaymentOverview from '@/pages/InvoiceAdjustmentRule/InvoiceAdjustmentPaymentOverview';
import InvoiceAdjustmentRuleOverview from '@/pages/InvoiceAdjustmentRule/InvoiceAdjustmentRuleOverview';
import { FeatureManager } from '@/directives';
import { ProcessChargeOverviewToolbar } from './components';
import ProcessChargesStops from './ProcessChargesStops';
import ProcessChargesShipments from './ProcessChargesShipments';
import ProcessChargesTrips from './ProcessChargesTrips';
import ProcessChargesOrders from './ProcessChargesOrders';

export default {
    name: 'ReviewChargesOverview',
    mixins: [GeneralMixin, FeatureManager],
    components: {
        ProcessChargesStops,
        ProcessChargesShipments,
        ProcessChargesTrips,
        ProcessChargesOrders,
        Tabs,
        Collapse,
        ProcessChargeOverviewToolbar,
        InvoiceAdjustmentPaymentOverview,
        InvoiceAdjustmentRuleOverview
    },
    data() {
        return {
            tabNames: [],
            tabRequiredFeature: [
                { name: 'Review Stops', featureId: null },
                { name: 'Review Shipments', featureId: null },
                { name: 'Review Trips', featureId: null },
                { name: 'Review Orders', featureId: null },
                { name: 'Team Member Adjustments', featureId: FEATURE_NAMES.InvoiceAdjustments },
                { name: 'Customer Adjustments', featureId: FEATURE_NAMES.InvoiceAdjustments },
                { name: 'Schedule Adjustments', featureId: FEATURE_NAMES.InvoiceAdjustments }
            ],
            tabComponents: [
                REVIEW_CHARGES_ACTIVE_FILTERS.ProcessChargesStops,
                REVIEW_CHARGES_ACTIVE_FILTERS.ProcessChargesShipments,
                REVIEW_CHARGES_ACTIVE_FILTERS.ProcessChargesTrips,
                REVIEW_CHARGES_ACTIVE_FILTERS.ProcessChargesOrders,
                REVIEW_CHARGES_ACTIVE_FILTERS.InvoiceAdjustmentPaymentOverview,
                REVIEW_CHARGES_ACTIVE_FILTERS.InvoiceAdjustmentPaymentOverview,
                REVIEW_CHARGES_ACTIVE_FILTERS.InvoiceAdjustmentRuleOverview
            ],
            activeTab: this.$route.params.type || 'review-stops',
            filters: {
                teamRegionId: null,
                customerId: null,
                totalCostFilter: 0,
                totalChargeFilter: 0,
                userId: null,
                status: 'All',
                fromDate: null,
                toDate: null,
                search: null,
                selectedTeamMember: null,
                attempt: null,
                approvalStatus: 'Unapproved',
                runNumber: null,
                invoiceAdjustmentStatusId: null,
                invoiceAdjustmentCategoryId: null
            },
            isCostApprovalFeatureEnabled: false,
            isChargeApprovalFeatureEnabled: false,
            servicePackages: [],
            selectedTotalChargeDropdowns: PROCESS_TOTALS_CONSTANTS[0],
            selectedTotalCostDropdowns: PROCESS_TOTALS_CONSTANTS[0],
            disableFilter: this.$route.params.type === 'schedule-adjustments',
            signalRHandlers: [],
            signalRTimeout: null
        };
    },
    async mounted() {
        this.servicePackages = [...(await this.getServicePackages())];
        this.loadCapacityList = this.user.vehicleCapacityUnitsConfiguration || [];

        this.activeTabs();

        this.signalRHandlers.push(
            {
                action: 'ProcessRecalculationCompleted',
                handler: async (totalSuccess, failedRecords) => {
                    this.$refs.processChargeTab[0].handleRecalculationCompleted(totalSuccess, failedRecords);
                }
            },
            {
                action: 'ProcessRecalculationTotal',
                handler: async (totalsToBeCalculated) => {
                    this.$refs.processChargeTab[0].handleTotalToBeRecalculated(totalsToBeCalculated);
                }
            },
            {
                action: 'ProcessRecalculationFailed',
                handler: (error) => {
                    this.$refs.processChargeTab[0].handleRecalculationFailed(error);
                }
            },
            // ORDERS SPECIFIC
            {
                action: 'ProcessRecalculationOrderComplete',
                handler: async (orderId) => {
                    if (this.activeTab === 'review-orders') {
                        this.$refs.processChargeTab[0].handleOrderRecalculated(orderId);
                    }
                }
            },
            {
                action: 'OrderRatesUpdated',
                handler: async (orderId, updateCost, updateCharges) => {
                    if (this.activeTab === 'review-orders') {
                        this.$refs.processChargeTab[0].handleOrderRatesUpdated(orderId, updateCost, updateCharges);
                    }
                }
            },
            {
                action: 'OrderApprovalStatusUpdated',
                handler: async (orderId, approved, unapproved) => {
                    if (this.activeTab === 'review-orders') {
                        this.$refs.processChargeTab[0].onOrderApprovalStatusUpdated(orderId, approved, unapproved);
                    }
                }
            },
            // STOPS SPECIFIC
            {
                action: 'ProcessRecalculationStopComplete',
                handler: async (stopId) => {
                    if (this.activeTab === 'review-stops') {
                        this.$refs.processChargeTab[0].handleStopRecalculated(stopId);
                    }
                }
            },
            {
                action: 'StopRatesUpdated',
                handler: async (stopId, updateCost, updateCharges) => {
                    if (this.activeTab === 'review-stops') {
                        this.$refs.processChargeTab[0].handleStopRatesUpdated(stopId, updateCost, updateCharges);
                    }
                }
            },
            {
                action: 'StopApprovalStatusUpdated',
                handler: async (stopId, approved, unapproved) => {
                    if (this.activeTab === 'review-stops') {
                        this.$refs.processChargeTab[0].onStopApprovalStatusUpdated(stopId, approved, unapproved);
                    }
                }
            },
            // SHIPMENTS SPECIFIC
            {
                action: 'ProcessRecalculationShipmentComplete',
                handler: async (shipmentId) => {
                    if (this.activeTab === 'review-shipments') {
                        this.$refs.processChargeTab[0].handleShipmentRecalculated(shipmentId);
                    }
                }
            },
            {
                action: 'ShipmentRatesUpdated',
                handler: async (shipmentId, updateCost, updateCharges) => {
                    if (this.activeTab === 'review-shipments') {
                        this.$refs.processChargeTab[0].handleShipmentRatesUpdated(
                            shipmentId,
                            updateCost,
                            updateCharges
                        );
                    }
                }
            },
            {
                action: 'ShipmentApprovalStatusUpdated',
                handler: async (shipmentId, approved, unapproved) => {
                    if (this.activeTab === 'review-shipments') {
                        this.$refs.processChargeTab[0].onShipmentApprovalStatusUpdated(
                            shipmentId,
                            approved,
                            unapproved
                        );
                    }
                }
            },
            // TRIP SPECIFIC
            {
                action: 'ProcessRecalculationTripComplete',
                handler: async (tripId) => {
                    if (this.activeTab === 'review-trips') {
                        this.$refs.processChargeTab[0].handleTripRecalculation(tripId);
                    }
                }
            },
            {
                action: 'TripApprovalStatusUpdated',
                handler: async (tripId, approved, unapproved) => {
                    if (this.activeTab === 'review-trips') {
                        this.$refs.processChargeTab[0].onTripApprovalStatusUpdated(tripId, approved, unapproved);
                    }
                }
            }
        );

        this.setupSignalR();
    },
    watch: {
        '$route.params.type': function(newVal) {
            this.disableFilter = this.$route.params.type === 'schedule-adjustments';
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            isReadOnlyUser: 'user/isReadOnlyUser',
            getConnectionStatus: 'signalR/getConnectionStatus',
            getConnection: 'signalR/getConnection'
        })
    },
    beforeDestroy() {
        clearTimeout(this.signalRTimeout);
        this.removeSignalRHandlers();
    },
    methods: {
        async switchTab(panel) {
            const tab = panel
                .toLowerCase()
                .split(' ')
                .join('-');

            this.activeTab = tab;
            this.$router.replace({ path: `/processcharges/list/${tab}`, query: {} });
        },
        replaceDashToSpacesAndCapitalizeFirstLetter(str) {
            return str
                .toLowerCase()
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        async handleFilterChanged(data) {
            this.filters = {
                ...this.filters,
                ...data,
                totalCostFilter: this.selectedTotalCostDropdowns.key,
                totalChargeFilter: this.selectedTotalChargeDropdowns.key
            };
        },
        async getServicePackages() {
            try {
                const endpoint = `/api/rate-groups/rateRules`;
                const response = await handleRequests(endpoint);

                if (response !== null && response.data !== null) {
                    return response.data.map((rg) => {
                        return {
                            rateGroupId: rg.rateGroupId,
                            rateGroupName: rg.rateGroupName,
                            rateGroupType: rg.ruleType
                        };
                    });
                }

                return null;
            } catch (e) {
                const message = `Unable to fetch service packages.`;
                showErrorMessage(this, message, e);
            }

            return null;
        },
        activeTabs() {
            const activeTabs = [];
            this.tabRequiredFeature.forEach((tab) => {
                if (tab.featureId === null) {
                    activeTabs.push(tab.name);
                } else if (this.$_hasFeature(tab.featureId)) {
                    activeTabs.push(tab.name);
                }
            });

            this.tabNames = activeTabs;
        },
        updateTotalCostDropDown(value) {
            this.selectedTotalCostDropdowns = value;
        },
        updateTotalChargeDropDown(value) {
            this.selectedTotalChargeDropdowns = value;
        },
        async setupSignalR() {
            try {
                await this.$store.dispatch('signalR/connect', HUB_NAMES.RatesEngineUpdateHub);
                const connection = this.getConnection(HUB_NAMES.RatesEngineUpdateHub);

                // Verify connection is established before proceeding
                if (!connection || connection.state !== SIGNALR_CONNECTION_STATE.Connected) {
                    throw new Error('SignalR connection not established');
                }

                await connection.invoke(HUB_ACTIONS.JoinToTeamChannel);
                await this.$store.dispatch('signalR/registerHandlers', {
                    hubName: HUB_NAMES.RatesEngineUpdateHub,
                    handlers: this.signalRHandlers
                });
            } catch (e) {
                console.error('Error connecting to rates engine update hub:', e);
            }
        },
        removeSignalRHandlers() {
            try {
                const connection = this.getConnection(HUB_NAMES.RatesEngineUpdateHub);

                if (connection) {
                    this.$store.dispatch('signalR/unregisterHandlers', {
                        hubName: HUB_NAMES.RatesEngineUpdateHub,
                        handlers: this.signalRHandlers
                    });
                    connection.invoke(HUB_ACTIONS.RemoveFromTeamChannel);
                }
            } catch (error) {
                console.error('Error removing SignalR handlers:', error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.custom-tab-list {
    padding: 10px;
    position: initial;
}

.review-contents {
    display: contents !important;
}

.collapse-panel {
    ::v-deep i.md-icon.md-icon-font.md-theme-default {
        float: left !important;
        position: initial !important;
        margin-top: -2px;
    }

    ::v-deep i.md-icon.md-icon-font.md-icon-image.md-date-icon.md-theme-default {
        margin-top: 6px !important;
    }
}

.collapse-panel-zindex {
    z-index: 0;
}

.ontop {
    z-index: 2;
}
</style>
